import { DefaultRootState } from '@databases/redux';
import { ObjectMode } from '@databases/types';
import {
  Database,
  DatabaseField,
  DatabaseFieldType,
  DatabaseValue,
  EntryWithContext,
  ValueWithContext,
} from '@databases/types/database';
import {
  DEFAULT_DATABASE_VIEW_SETTINGS,
  DEFAULT_FIELD_VISIBILITY_ID,
  DatabaseCalculationType,
  DatabaseCardWidth,
  DatabaseCoverFieldType,
  DatabaseFilter,
  DatabaseFilterType,
  DatabaseFilterUsageType,
  DatabaseLayout,
  DatabaseLayoutType,
  DatabaseView,
  DatabaseViewSettings,
  ViewId,
  shouldShowField,
} from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { clone, cloneDeep, omit } from 'lodash-es';
import { Op } from 'quill-delta';

import { generateId, isEqualOmitUndefined } from '../../utils';
import { DatabaseChange, ValueChange } from '../changesets';
import {
  getNumberRepresentation,
  getStringRepresentation,
  isEmptyValue,
  isEqualValue,
} from '../field-types';
import { toDisplayDate } from '../field-types/date';
import {
  generateAllValues,
  getConvertedDatabaseValue,
  getDatabaseEntryOptional,
  getDatabaseEntryRequired,
  getDatabaseFieldOptional,
  getDatabaseFields,
} from '../utils';

/**
 * Returns true if the given database entry is matched by the given database filter.
 * If the database filter references a field that does not exist (anymore), or the filter has an unknown type,
 * true is returned. This is to make sure that when a filter is not fully configured yet (for example no field
 * has been selected yet), or when a field that is referenced by a filter has been removed, the filter does not
 * cause the database to be empty.
 */
export function matchesFilter(
  { database, entry }: EntryWithContext,
  filter: DatabaseFilter,
  state: DefaultRootState | undefined
): boolean {
  switch (filter.type) {
    case DatabaseFilterType.AND: {
      return filter.value.every((f) => matchesFilter({ database, entry }, f, state));
    }

    case DatabaseFilterType.OR: {
      return filter.value.some((f) => matchesFilter({ database, entry }, f, state));
    }

    case DatabaseFilterType.EQUALS: {
      const field = getDatabaseFieldOptional(database, filter.fieldId);
      if (!field) {
        return true;
      }
      const value = getConvertedDatabaseValue(database, field, entry, state);
      const result = isEqualValue(
        { database, field, value: filter.value },
        { database, field, entry, value },
        state
      );
      return filter.not ? !result : result;
    }

    case DatabaseFilterType.CONTAINS: {
      const field = getDatabaseFieldOptional(database, filter.fieldId);
      if (!field) {
        return true;
      }
      const value = field && getConvertedDatabaseValue(database, field, entry, state);
      const result = value
        ? (getStringRepresentation({ database, field, entry, value }, state) || '')
            .toLowerCase()
            .includes(filter.value.toLowerCase())
        : true;
      return filter.not ? !result : result;
    }

    case DatabaseFilterType.EMPTY: {
      const field = getDatabaseFieldOptional(database, filter.fieldId);
      if (!field) {
        return true;
      }
      const value = field && getConvertedDatabaseValue(database, field, entry, state);
      const result = value
        ? isEmptyValue({ database, field, entry, value }, state)
        : true;
      return filter.not ? !result : result;
    }

    case DatabaseFilterType.GREATER: {
      const field = getDatabaseFieldOptional(database, filter.fieldId);
      if (!field) {
        return true;
      }
      const value = field && getConvertedDatabaseValue(database, field, entry, state);
      const valueNumber = getNumberRepresentation(
        { database, field, entry, value },
        state
      );
      const filterNumber = getNumberRepresentation(
        { database, field, entry, value: filter.value },
        state
      );
      return (
        valueNumber != null &&
        filterNumber != null &&
        (filter.orEqual ? valueNumber >= filterNumber : valueNumber > filterNumber)
      );
    }

    case DatabaseFilterType.LESS: {
      const field = getDatabaseFieldOptional(database, filter.fieldId);
      if (!field) {
        return true;
      }
      const value = field && getConvertedDatabaseValue(database, field, entry, state);
      const valueNumber = getNumberRepresentation(
        { database, field, entry, value },
        state
      );
      const filterNumber = getNumberRepresentation(
        { database, field, entry, value: filter.value },
        state
      );
      return (
        valueNumber != null &&
        filterNumber != null &&
        (filter.orEqual ? valueNumber <= filterNumber : valueNumber < filterNumber)
      );
    }

    default:
      return true;
  }
}

/**
 * Returns the "number of active filters" as it should be presented for the user for the specified database filter.
 */
export function getFilterCount(filter: DatabaseFilter | undefined): number {
  if (filter == null) {
    return 0;
  } else if (
    filter.type === DatabaseFilterType.AND ||
    filter.type === DatabaseFilterType.OR
  ) {
    return filter.value.reduce((c, f) => c + getFilterCount(f), 0);
  } else {
    return 1;
  }
}

/**
 * Compares two strings or numbers and returns a negative number if the second one is greater, a positive number if the
 * first one is greater, or zero if both are equal.
 * If both arguments are numbers (in number or string form), they are compared numerically. Otherwise, they are compared
 * naturally (alphabetical segments are compared alphabetically, numerical segments are compared numerically).
 *
 * One implication of this for now is that pure numbers support decimals, negatives and thousands separators, while number
 * segments in mixed values don't. For example, "-10" is less than "-2", while "JIRA-10" is greater than "JIRA-2" (despite
 * the minus sign in both cases). Also, "1.10" is less than "1.2", while "JIRA-1.10" is greater than "JIRA-1.2" (since in
 * the mixed value the number segments are compared separately).
 *
 * Following international standards, a full stop and a comma are supported as decimal separators, and a space and thin space
 * (along with their non-breaking variants) are supported as thousands separators. Full-stops and commas are not supported
 * as thousands separators, as they are impossible to distinguish from decimal separators.
 */
export function compareStringOrNumber(
  numberOrStringA: string | number,
  numberOrStringB: string | number,
  ascending: boolean
): number {
  const [a, b] = ascending
    ? [numberOrStringA, numberOrStringB]
    : [numberOrStringB, numberOrStringA];
  const [numberA, numberB] = [a, b].map((numberOrString) =>
    typeof numberOrString === 'number'
      ? numberOrString
      : Number(
          numberOrString
            .trim()
            .replace(/[,]/g, '.') // Normalize decimal separator
            .replace(/[\u2212]/g, '-') // Normalize minus (Unicode minus)
            .replace(/[ \u2009\u202F\xa0']/g, '') // Remove thousands separators (space, thin space, non-breaking thin space, non-breaking space, apostrophe)
        )
  );

  if (!isNaN(numberA) && !isNaN(numberB)) {
    return numberA - numberB;
  } else {
    return `${a}`.localeCompare(`${b}`, 'en-US', { caseFirst: 'upper', numeric: true });
  }
}

/**
 * Returns a copy of the database draft, with the filters and sort options of the given database view applied. filter, sort and fields
 * settings are applied to the saved version of the database to make sure that nothing jumps around while editing values, but the resulting
 * view contains the values of the database draft.
 * @param savedDatabase The current saved version of the database.
 * @param databaseDraft The current draft of the database. May be set to the same as savedDatabase if no draft exists.
 */
export function applyDatabaseView<Db extends Database>(
  savedDatabase: Database,
  databaseDraft: Db,
  viewSettings: DatabaseViewSettings = DEFAULT_DATABASE_VIEW_SETTINGS,
  state: DefaultRootState | undefined
): Db {
  let result = { ...databaseDraft };

  if (viewSettings.filter) {
    result = {
      ...result,
      entries: result.entries.filter((entryId) => {
        const savedEntry = getDatabaseEntryOptional(savedDatabase, entryId);
        if (!savedEntry) {
          return true;
        } else {
          return matchesFilter(
            { database: savedDatabase, entry: savedEntry },
            viewSettings.filter!,
            state
          );
        }
      }),
    };
  }

  if (viewSettings.sort) {
    const sortField = getDatabaseFieldOptional(databaseDraft, viewSettings.sort!.fieldId);
    if (sortField) {
      result = {
        ...result,
        entries: [...result.entries].sort((entryIdA, entryIdB) => {
          const aIsNew = !savedDatabase.entries.includes(entryIdA);
          const bIsNew = !savedDatabase.entries.includes(entryIdB);
          // Newly added entries are always put at the end, in their original position
          if (aIsNew && bIsNew) {
            return (
              savedDatabase.entries.indexOf(entryIdA) -
              savedDatabase.entries.indexOf(entryIdB)
            );
          } else if (aIsNew && !bIsNew) {
            return 1;
          } else if (!aIsNew && bIsNew) {
            return -1;
          } else {
            const entryA = getDatabaseEntryRequired(savedDatabase, entryIdA);
            const entryB = getDatabaseEntryRequired(savedDatabase, entryIdB);
            const contextA = {
              database: savedDatabase,
              field: sortField,
              entry: entryA,
              value: getConvertedDatabaseValue(savedDatabase, sortField, entryA, state),
            };
            const contextB = {
              database: savedDatabase,
              field: sortField,
              entry: entryB,
              value: getConvertedDatabaseValue(savedDatabase, sortField, entryB, state),
            };
            return compareStringOrNumber(
              getNumberRepresentation(contextA, state) ??
                (getStringRepresentation(contextA, state) || ''),
              getNumberRepresentation(contextB, state) ??
                (getStringRepresentation(contextB, state) || ''),
              viewSettings.sort!.ascending
            );
          }
        }),
      };
    }
  }

  if (viewSettings.fields) {
    result = {
      ...result,
      fields: result.fields.filter(
        (fieldId) =>
          !savedDatabase.fields.includes(fieldId) || // Always show newly added fields, regardless of field selection
          shouldShowField(viewSettings.fields, fieldId)
      ),
    };
  }

  if (viewSettings.search) {
    result = {
      ...result,
      entries: result.entries.filter((entryId) => {
        const entry = getDatabaseEntryRequired(result, entryId);
        return getDatabaseFields(result).some((field) => {
          const value = getConvertedDatabaseValue(result, field, entry, state);

          const displayFormattedDate =
            value.type === DatabaseFieldType.DATE &&
            value.value &&
            toDisplayDate(value.value);

          const stringRepresentation = displayFormattedDate
            ? displayFormattedDate
            : getStringRepresentation({ database: result, field, entry, value }, state);

          return value
            ? (stringRepresentation || '')
                .toLowerCase()
                .includes(viewSettings.search!.toLowerCase())
            : true;
        });
      }),
    };
  }

  return result;
}

/**
 * Returns database values by field ID that attempt to match the given filter. This is only possible for
 * some filter types (in particular an equals filter). The purpose is to set these values when creating
 * a new database entry so that the new entry matches the current filter.
 */
export function getDefaultValuesForFilter(
  filter: DatabaseFilter | undefined
): Record<string, DatabaseValue> {
  const result: Record<string, DatabaseValue> = {};

  switch (filter?.type) {
    case DatabaseFilterType.AND:
    case DatabaseFilterType.OR:
      for (const f of filter.value) {
        Object.assign(result, getDefaultValuesForFilter(f));
      }
      break;

    case DatabaseFilterType.EQUALS:
      if (!filter.not) {
        result[filter.fieldId] = clone(filter.value);
      }
      break;
  }

  return result;
}

/**
 * Get only database values of the database which has the same fieldId as the predefined filters.
 */
export const getFilteredValuesWithContext = (
  database: Database,
  filter: DatabaseFilter
): ValueWithContext[] => {
  return [...generateAllValues(database)].filter((context) => {
    if (filter.type === DatabaseFilterType.AND || filter.type === DatabaseFilterType.OR) {
      return !!filter.value.find((f) => {
        switch (f.type) {
          case DatabaseFilterType.EQUALS:
          case DatabaseFilterType.CONTAINS:
          case DatabaseFilterType.EMPTY:
          case DatabaseFilterType.GREATER:
          case DatabaseFilterType.LESS: {
            return f.fieldId === context.field.id;
          }
        }
      });
    }
  });
};

/**
 * Returns true if the given database field can be used as a group field (that is, the field is a single-select field).
 */
export function isValidGroupField(groupField: DatabaseField): boolean {
  return groupField.type === DatabaseFieldType.SELECT && !groupField.settings.multi;
}

export function isValidDateField(groupField: DatabaseField): boolean {
  return groupField.type === DatabaseFieldType.DATE;
}

/**
 * Returns the group field configured in the given layout settings. If no valid group field is configured, undefined is returned.
 */
export function getGroupField(
  database: Database<ObjectMode>,
  layout: DatabaseLayout
): DatabaseField | undefined {
  const groupField = getDatabaseFieldOptional(database, layout.groupFieldId);
  return groupField && isValidGroupField(groupField) ? groupField : undefined;
}

/**
 * Returns the date field configured in the given layout settings. If no valid date field is configured, undefined is returned.
 */
export function getDateField(
  database: Database<ObjectMode>,
  layout: DatabaseLayout
): DatabaseField | undefined {
  const dateField = getDatabaseFieldOptional(database, layout.dateFieldId);
  return dateField && isValidDateField(dateField) ? dateField : undefined;
}

export function getViewById(
  views: DatabaseView[],
  viewId: string | null
): DatabaseView | undefined {
  return views.find((value) => value.id === viewId);
}

export function getDefaultView(database: Database): DatabaseView {
  return (
    database.views.find((view) => view.id === database.defaultViewId) || database.views[0]
  );
}

export function viewHasChanges(
  viewSettings: DatabaseViewSettings | undefined,
  other: DatabaseViewSettings | undefined
): boolean {
  return !isEqualOmitUndefined(omit(viewSettings, ['search']), omit(other, ['search']));
}

export function createView(
  viewSettings: DatabaseViewSettings = DEFAULT_DATABASE_VIEW_SETTINGS
): DatabaseView {
  const id = generateId();
  return {
    id,
    settings: viewSettings,
    name: '',
  };
}

export function isUnsavedView(view: DatabaseView): boolean {
  return view?.id === ViewId.UNSAVED;
}

export enum ViewFilterChangeType {
  VIEW_FILTERS_SET_FILTER_USAGE_TYPE = 'view_filters_set_filter_usage_type',
  VIEW_FILTERS_SET_FIELD_ID = 'view_filters_set_field_id',
  VIEW_FILTERS_SET_FILTER_TYPE = 'view_filters_set_filter_type',
  VIEW_FILTERS_SET_VALUE = 'view_filters_set_value',
}

export enum ViewChangeTypes {
  LAYOUT_TYPE = 'layout_type',
  LAYOUT_CARD_WIDTH = 'layout_card_width',
  LAYOUT_COVER_FIELD_ID = 'layout_cover_field_id',
  LAYOUT_GROUP_FIELD_ID = 'layout_group_field_id',
  LAYOUT_DATE_FIELD_ID = 'layout_date_field_id',
  LAYOUT_NUMBERED_ENTRIES_SET = 'layout_numbered_entries_set',
  VIEW_NAME = 'view_name',
  VIEW_FIELDS = 'view_fields',
  VIEW_SORT = 'view_sort',
  VIEW_FILTERS_SET = 'view_filters_set',
  VIEW_FILTERS_APPEND = 'view_filters_append',
  VIEW_FILTERS_REMOVE = 'view_filters_remove',
  VIEW_SEARCH = 'view_search',
  VIEW_CALCULATIONS_SET = 'view_calculations_set',
  VIEW_CALCULATIONS_REMOVE = 'view_calculations_remove',
}

export type ViewChangeType =
  | {
      type: ViewChangeTypes.LAYOUT_TYPE;
      value: DatabaseLayoutType;
    }
  | {
      type: ViewChangeTypes.LAYOUT_CARD_WIDTH;
      value: DatabaseCardWidth;
    }
  | {
      type: ViewChangeTypes.LAYOUT_COVER_FIELD_ID;
      value: DatabaseCoverFieldType | string;
    }
  | {
      type: ViewChangeTypes.LAYOUT_GROUP_FIELD_ID;
      value: string | undefined;
    }
  | {
      type: ViewChangeTypes.LAYOUT_DATE_FIELD_ID;
      value: string | undefined;
    }
  | {
      type: ViewChangeTypes.LAYOUT_NUMBERED_ENTRIES_SET;
      value: boolean;
    }
  | {
      type: ViewChangeTypes.VIEW_NAME;
      value: string;
      delta: Op[];
    }
  | {
      type: ViewChangeTypes.VIEW_FIELDS;
      fieldKey: string;
      fieldValue: boolean;
      fields: DatabaseField[] | undefined;
    }
  | {
      type: ViewChangeTypes.VIEW_SORT;
      sort:
        | {
            fieldId: string;
            ascending: boolean;
          }
        | undefined;
    }
  | {
      type:
        | ViewChangeTypes.VIEW_FILTERS_SET
        | ViewFilterChangeType.VIEW_FILTERS_SET_FIELD_ID
        | ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_TYPE;
      filter: DatabaseFilter | undefined;
      path?: number[];
    }
  | {
      type: ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_USAGE_TYPE;
      filterUsageType: DatabaseFilterUsageType;
    }
  | {
      type: ViewFilterChangeType.VIEW_FILTERS_SET_VALUE;
      value: DatabaseFilter[] | DatabaseValue | string;
      path?: number[];
      change: ValueChange | null;
    }
  | {
      type: ViewChangeTypes.VIEW_FILTERS_APPEND;
      filter: DatabaseFilter;
      path?: number[];
    }
  | {
      type: ViewChangeTypes.VIEW_FILTERS_REMOVE;
      path?: number[];
    }
  | {
      type: ViewChangeTypes.VIEW_SEARCH;
      value: string | undefined;
    }
  | {
      type: ViewChangeTypes.VIEW_CALCULATIONS_SET;
      fieldKey: string;
      value: DatabaseCalculationType;
    }
  | {
      type: ViewChangeTypes.VIEW_CALCULATIONS_REMOVE;
      fieldKey: string;
    };

// Returns a nested filter given a path and the index of path where the filter is located
const getTargetedFilter = (
  filter: DatabaseFilter,
  path: number[],
  append?: boolean
): [currentFilter: DatabaseFilter, idx: number] => {
  let currentFilter: DatabaseFilter = filter;
  let pathDepth = 0;
  for (pathDepth; pathDepth < (append ? path.length : path.length - 1); pathDepth++) {
    const index = path[pathDepth];
    if (
      index !== undefined &&
      (currentFilter.type === DatabaseFilterType.AND ||
        currentFilter.type === DatabaseFilterType.OR)
    ) {
      currentFilter = currentFilter.value[index];
    } else {
      break;
    }
  }

  return [currentFilter, pathDepth];
};

export function applyViewChange(view: DatabaseView, data: ViewChangeType): DatabaseView {
  if (data.type === ViewChangeTypes.VIEW_NAME) {
    return {
      ...view,
      name: data.value,
    };
  } else {
    return {
      ...view,
      settings: applyViewSettingsChange(view.settings, data),
    };
  }
}

export function applyViewSettingsChange(
  viewSettings: DatabaseViewSettings,
  data: ViewChangeType
): DatabaseViewSettings {
  switch (data.type) {
    case ViewChangeTypes.LAYOUT_TYPE: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          type: data.value,
        },
      };
    }
    case ViewChangeTypes.LAYOUT_CARD_WIDTH: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          cardWidth: data.value,
        },
      };
    }
    case ViewChangeTypes.LAYOUT_COVER_FIELD_ID: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          coverFieldId: data.value,
        },
      };
    }
    case ViewChangeTypes.LAYOUT_GROUP_FIELD_ID: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          groupFieldId: data.value,
        },
      };
    }
    case ViewChangeTypes.LAYOUT_DATE_FIELD_ID: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          dateFieldId: data.value,
        },
      };
    }
    case ViewChangeTypes.LAYOUT_NUMBERED_ENTRIES_SET: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          showNumberedEntries: data.value,
        },
      };
    }
    case ViewChangeTypes.VIEW_FIELDS: {
      let newFields: Record<string, boolean> = {};
      if (viewSettings.fields) {
        newFields = viewSettings.fields;
      } else {
        if (data.fields) {
          newFields = data.fields.reduce((obj, field) => {
            obj[field.id] = true;
            return obj;
          }, {} as Record<string, boolean>);
        }
      }

      newFields = {
        ...newFields,
        [data.fieldKey]: data.fieldValue,
        [DEFAULT_FIELD_VISIBILITY_ID]: true, // for the current view, we want newly added fields to be visible
      };

      return {
        ...viewSettings,
        fields:
          data.fields &&
          data.fields.every((field) => field.id in newFields) &&
          Object.keys(newFields).every(
            (fieldId) =>
              fieldId === DEFAULT_FIELD_VISIBILITY_ID || newFields[fieldId] === true
          )
            ? undefined
            : newFields,
      };
    }
    case ViewChangeTypes.VIEW_SORT: {
      return {
        ...viewSettings,
        sort: data.sort,
      };
    }
    case ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_USAGE_TYPE:
      if (
        viewSettings.filter &&
        (viewSettings.filter.type === DatabaseFilterType.AND ||
          viewSettings.filter.type === DatabaseFilterType.OR)
      ) {
        return {
          ...viewSettings,
          filter: {
            type: data.filterUsageType,
            value: cloneDeep(viewSettings.filter.value),
          },
        };
      } else {
        return {
          ...viewSettings,
          filter: undefined,
        };
      }

    case ViewFilterChangeType.VIEW_FILTERS_SET_VALUE:
      if (!viewSettings.filter || data.path === undefined) {
        return {
          ...viewSettings,
          filter: undefined,
        };
      } else {
        const newFilterValue = cloneDeep(viewSettings.filter);

        const [currentFilter, pathDepth] = getTargetedFilter(newFilterValue, data.path);

        if (
          currentFilter.type === DatabaseFilterType.AND ||
          currentFilter.type === DatabaseFilterType.OR
        ) {
          currentFilter.value = [
            ...currentFilter.value.slice(0, data.path[pathDepth]),
            {
              ...currentFilter.value[data.path[pathDepth]],
              value: data.value,
            } as DatabaseFilter,
            ...currentFilter.value.slice(data.path[pathDepth] + 1),
          ];
        }

        return {
          ...viewSettings,
          filter: newFilterValue,
        };
      }

    case ViewChangeTypes.VIEW_FILTERS_SET:
    case ViewFilterChangeType.VIEW_FILTERS_SET_FIELD_ID:
    case ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_TYPE:
      if (!data.filter) {
        return {
          ...viewSettings,
          filter: undefined,
        };
      }

      if (viewSettings.filter && data.path) {
        const newFilterValue = cloneDeep(viewSettings.filter);

        const [currentFilter, pathDepth] = getTargetedFilter(newFilterValue, data.path);

        if (
          currentFilter.type === DatabaseFilterType.AND ||
          currentFilter.type === DatabaseFilterType.OR
        ) {
          currentFilter.value = [
            ...currentFilter.value.slice(0, data.path[pathDepth]),
            cloneDeep(data.filter),
            ...currentFilter.value.slice(data.path[pathDepth] + 1),
          ];
        }

        return {
          ...viewSettings,
          filter: newFilterValue,
        };
      } else {
        return {
          ...viewSettings,
        };
      }

    case ViewChangeTypes.VIEW_FILTERS_APPEND: {
      if (!viewSettings.filter) {
        return {
          ...viewSettings,
          filter: {
            type: DatabaseFilterType.AND,
            value: [cloneDeep(data.filter)],
          },
        };
      } else {
        const newFilterValue = cloneDeep(viewSettings.filter);

        const currentFilter: DatabaseFilter = data.path
          ? getTargetedFilter(newFilterValue, data.path, true)[0]
          : newFilterValue;

        if (
          currentFilter.type === DatabaseFilterType.AND ||
          currentFilter.type === DatabaseFilterType.OR
        ) {
          currentFilter.value = [...currentFilter.value, cloneDeep(data.filter)];
        }

        return {
          ...viewSettings,
          filter: newFilterValue,
        };
      }
    }

    case ViewChangeTypes.VIEW_FILTERS_REMOVE: {
      if (!viewSettings.filter || !data.path) {
        return {
          ...viewSettings,
          filter: undefined,
        };
      } else {
        const newFilterValue = cloneDeep(viewSettings.filter);

        const [currentFilter, pathDepth] = getTargetedFilter(newFilterValue, data.path);

        if (
          currentFilter.type === DatabaseFilterType.AND ||
          currentFilter.type === DatabaseFilterType.OR
        ) {
          currentFilter.value = [
            ...currentFilter.value.slice(0, data.path[pathDepth]),
            ...currentFilter.value.slice(data.path[pathDepth] + 1),
          ];

          if (currentFilter.value.length === 0 && pathDepth === 0) {
            return {
              ...viewSettings,
              filter: undefined,
            };
          }
        }

        return {
          ...viewSettings,
          filter: newFilterValue,
        };
      }
    }

    case ViewChangeTypes.VIEW_SEARCH: {
      return {
        ...viewSettings,
        search: data.value,
      };
    }

    case ViewChangeTypes.VIEW_CALCULATIONS_SET: {
      return {
        ...viewSettings,
        calculations: {
          ...viewSettings.calculations,
          [data.fieldKey]: data.value,
        },
      };
    }

    case ViewChangeTypes.VIEW_CALCULATIONS_REMOVE: {
      return {
        ...viewSettings,
        calculations: omit(viewSettings.calculations, [data.fieldKey]),
      };
    }

    default: {
      return viewSettings;
    }
  }
}

export const getViewChange = (
  data: ViewChangeType,
  view: DatabaseView
): DatabaseChange | undefined => {
  switch (data.type) {
    case ViewChangeTypes.LAYOUT_TYPE:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_TYPE,
        viewId: view.id,
        newLayoutType: data.value,
      };
    case ViewChangeTypes.LAYOUT_CARD_WIDTH:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH,
        viewId: view.id,
        newCardWidth: data.value,
      };
    case ViewChangeTypes.LAYOUT_COVER_FIELD_ID:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_COVER_FIELD_ID,
        viewId: view.id,
        newCoverFieldId: data.value,
      };
    case ViewChangeTypes.LAYOUT_GROUP_FIELD_ID:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_GROUP_FIELD_ID,
        viewId: view.id,
        newGroupFieldId: data.value,
      };
    case ViewChangeTypes.LAYOUT_DATE_FIELD_ID:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_DATE_FIELD_ID,
        viewId: view.id,
        newDateFieldId: data.value,
      };
    case ViewChangeTypes.LAYOUT_NUMBERED_ENTRIES_SET:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES,
        viewId: view.id,
        newShowNumberedEntries: data.value,
      };
    case ViewChangeTypes.VIEW_NAME:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_NAME,
        viewId: view.id,
        newName: data.value,
        delta: data.delta,
      };
    case ViewChangeTypes.VIEW_FIELDS:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_FIELDS,
        viewId: view.id,
        fieldKey: data.fieldKey,
        fieldValue: data.fieldValue,
        existingFields: data.fields,
      };
    case ViewChangeTypes.VIEW_SORT:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_SORT,
        viewId: view.id,
        sort: data.sort,
      };
    case ViewChangeTypes.VIEW_FILTERS_SET:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_FILTER,
        viewId: view.id,
        newFilter: data.filter,
        path: data.path,
      };
    case ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_USAGE_TYPE:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_FILTER_USAGE_TYPE,
        viewId: view.id,
        newFilterUsageType: data.filterUsageType,
      };
    case ViewFilterChangeType.VIEW_FILTERS_SET_FIELD_ID:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_FILTER_FIELD_ID,
        viewId: view.id,
        newFilter: data.filter,
        path: data.path,
      };
    case ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_TYPE:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_FILTER_TYPE,
        viewId: view.id,
        newFilter: data.filter,
        path: data.path,
      };
    case ViewFilterChangeType.VIEW_FILTERS_SET_VALUE:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_FILTER_VALUE,
        viewId: view.id,
        newFilterValue: data.value,
        path: data.path,
        change: data.change,
      };
      break;
    case ViewChangeTypes.VIEW_FILTERS_APPEND:
      return {
        type: DatabaseChangeType.APPEND_SAVED_VIEW_FILTER,
        viewId: view.id,
        newFilter: data.filter,
        path: data.path,
      };
    case ViewChangeTypes.VIEW_FILTERS_REMOVE:
      return {
        type: DatabaseChangeType.REMOVE_SAVED_VIEW_FILTER,
        viewId: view.id,
        path: data.path,
      };
    case ViewChangeTypes.VIEW_CALCULATIONS_SET:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS,
        viewId: view.id,
        fieldId: data.fieldKey,
        value: data.value,
      };
    case ViewChangeTypes.VIEW_CALCULATIONS_REMOVE:
      return {
        type: DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION,
        viewId: view.id,
        fieldId: data.fieldKey,
      };
  }
};

/**
 * Returns the count of hidden fields before the given index
 * */
export const getHiddenFieldsCountBeforeIndex = (
  hiddenFields: string[],
  allFields: readonly string[],
  idx: number
) =>
  hiddenFields.filter((hiddenField) => {
    const hiddenFieldIndex = allFields?.indexOf(hiddenField);
    return hiddenFieldIndex >= 0 && hiddenFieldIndex <= idx;
  }).length;

/**
 * Returns the hidden fields of the given view settings
 * */
export const getHiddenFields = (viewSettings: DatabaseViewSettings | undefined) =>
  Object.entries(viewSettings?.fields || {})
    .filter(([fieldId, value]) => !value && fieldId !== DEFAULT_FIELD_VISIBILITY_ID)
    .map(([key, _]) => key);

export const areAllFieldsHidden = (viewSettings: DatabaseViewSettings | undefined) =>
  viewSettings?.fields
    ? Object.entries(viewSettings.fields)
        .filter(([fieldId, _]) => fieldId !== DEFAULT_FIELD_VISIBILITY_ID)
        .every(([_, value]) => Boolean(!value))
    : false;

/**
 * If the database has hidden fields, the given index is incremented by the count of the hidden fields which are before the index
 * */
export const getNewIndexWithHiddenFields = (
  idx: number,
  allFields: readonly string[],
  hiddenFields: string[] | undefined
) => {
  if (hiddenFields?.length) {
    const hiddenFieldsCountBeforeIndex = getHiddenFieldsCountBeforeIndex(
      hiddenFields,
      allFields,
      idx
    );

    return hiddenFieldsCountBeforeIndex > 0 ? idx + hiddenFieldsCountBeforeIndex : idx;
  } else {
    return idx;
  }
};
